import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, Pause, Play, RefreshCw } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import {
  ACCESS_OPTIONS, BILLING_OPTIONS, accessProblem, enrolmentUrl, priceNote, priceProblem,
} from "../../lib/groupAccess";
import { useSettings } from "../../lib/settings";
import { FPED, REGIME_OPTIONS, isFped, regimeLock } from "../../lib/regime";

/**
 * "Acces și plată" in the cohort's form (07.10.2026): who can enrol, how a
 * place is paid, for which institution, at what price (09.10.2026), with the
 * Prima de Carieră Didactică card or without (12.10.2026) — and, once a
 * private cohort is saved, its link: copy, replace, pause.
 */
export default function GroupAccessBlock({ form, setForm, group, onLinkChanged, enrolled = 0 }) {
  const problem = accessProblem(form) || priceProblem(form.price);
  const settings = useSettings();
  const savedPrivate = group?.access === "link" && form.access === "link";
  // Curs FPED (12.10.2026): only behind a link, fixed once somebody has enrolled.
  const fped = isFped(form);
  const regimeLocked = regimeLock(form, enrolled, group);
  return (
    <div className="rounded-xl border border-[#E5E7EB] p-4" data-testid="group-access-block">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">Acces și plată</div>

      <fieldset className="mt-3">
        <legend className="text-sm font-semibold text-[#373A45]">Cine se poate înscrie</legend>
        <Choices name="access" options={ACCESS_OPTIONS} value={form.access}
          onChange={(v) => setForm({ ...form, access: v,
            ...(v === "public" && !(group && enrolled > 0) ? { regime: "acreditat" } : {}),
            ...(v === "public" ? { billing_mode: "individual" } : {}) })} />
      </fieldset>

      {(form.access === "link" || fped) && (
        <fieldset className="mt-4" data-testid="group-regime">
          <legend className="text-sm font-semibold text-[#373A45]">Regimul grupei</legend>
          <Choices name="regime" options={REGIME_OPTIONS} value={fped ? FPED : "acreditat"} disabled={Boolean(regimeLocked)}
            onChange={(v) => setForm({ ...form, regime: v, ...(v === FPED ? { prima_card: false } : {}) })} />
          <p className="mt-2 text-xs text-[#6B7280]" data-testid="group-regime-note">
            {regimeLocked || (fped
              ? "Sesiunile nu se verifică pe calendarul ministerului, grupa nu apare în registrul formabililor și în "
                + "exportul pentru minister, iar textele nu pomenesc acreditarea. Cursanții semnează contractul de "
                + "participare la curs, nu contractul de formare al programului acreditat."
              : "Se alege înainte de prima înscriere: după aceea nu se mai schimbă.")}
          </p>
        </fieldset>
      )}

      {form.access === "link" && (
        <>
          <fieldset className="mt-4">
            <legend className="text-sm font-semibold text-[#373A45]">Cum se plătește</legend>
            <Choices name="billing" options={BILLING_OPTIONS} value={form.billing_mode}
              onChange={(v) => setForm({ ...form, billing_mode: v })} />
          </fieldset>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-xs font-semibold text-[#373A45]">
              <span>Instituția{form.billing_mode === "institution" && <span className="text-[#9b1f2c]"> *</span>}</span>
              <input value={form.institution_name} onChange={(e) => setForm({ ...form, institution_name: e.target.value })}
                maxLength={160} placeholder="ex. Școala Gimnazială nr. 5 Cluj-Napoca"
                className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-normal"
                data-testid="group-institution-name" />
              <span className="font-normal text-[#6B7280]">
                Apare pe pagina de înscriere și completează singură câmpul „Unitate de învățământ”, blocat pentru cursant.
              </span>
            </label>
            <label className="grid gap-1 text-xs font-semibold text-[#373A45]">
              <span>Persoana de contact <span className="font-normal text-[#6B7280]">(doar în admin)</span></span>
              <input value={form.institution_contact} onChange={(e) => setForm({ ...form, institution_contact: e.target.value })}
                maxLength={300} placeholder="nume, telefon, email"
                className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-normal" />
            </label>
          </div>
        </>
      )}

      {/* The cohort's own price (09.10.2026): for any cohort, on the site or behind a link. */}
      <label className="mt-4 grid max-w-xs gap-1 text-xs font-semibold text-[#373A45]">
        <span>Preț special pentru această grupă <span className="font-normal text-[#6B7280]">(lei, pe persoană)</span></span>
        <input value={form.price ?? ""} onChange={(e) => setForm({ ...form, price: e.target.value })}
          inputMode="decimal" placeholder={settings.price ? `${settings.price}` : "prețul de pe site"}
          className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-normal tabular-nums"
          data-testid="group-price" />
      </label>
      <p className="mt-1 text-xs text-[#6B7280]" data-testid="group-price-note">
        {priceNote(form.price, settings.price, enrolled)}
      </p>

      {/* The Prima de Carieră Didactică card (12.10.2026): a switch on the cohort, on unless turned off. */}
      <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-[#373A45]">
        <input type="checkbox" checked={!fped && form.prima_card !== false} disabled={fped}
          onChange={(e) => setForm({ ...form, prima_card: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-[#965EA7]" data-testid="group-prima-card" />
        <span>
          <span className="font-semibold">Cursanții pot plăti cu cardul Prima de Carieră Didactică</span>
          <span className="block text-xs text-[#6B7280]" data-testid="group-prima-note">
            {fped
              ? "La un Curs FPED cardul nu se poate folosi: plătește doar cursuri acreditate."
              : form.prima_card !== false
              ? "Contractul, pagina de plată și emailul de confirmare pomenesc cardul, ca până acum."
              : "Pagina de plată și emailul de confirmare nu mai pomenesc cardul, iar din contract dispare paragraful care îl oferă (punctul 2). Restul contractului rămâne la fel."}
          </span>
        </span>
      </label>

      {problem && <p className="mt-3 text-xs font-semibold text-[#9b1f2c]" role="alert">{problem}</p>}

      {form.access === "link" && !savedPrivate && (
        <p className="mt-3 text-xs text-[#6B7280]">Linkul de înscriere apare aici după ce salvezi grupa.</p>
      )}
      {savedPrivate && <LinkPanel group={group} onChanged={onLinkChanged} />}
    </div>
  );
}

function Choices({ name, options, value, onChange, disabled = false }) {
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2">
      {options.map((o) => (
        <label key={o.value}
          className={`flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2.5 text-sm ${
            value === o.value ? "border-[#965EA7] bg-[#965EA7]/[0.05]" : "border-[#E5E7EB] hover:border-[#965EA7]/50"}`}>
          <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)}
            disabled={disabled}
            className="mt-0.5 h-4 w-4 accent-[#965EA7]" data-testid={`group-${name}-${o.value}`} />
          <span>
            <span className="font-semibold text-[#373A45]">{o.label}</span>
            <span className="block text-xs text-[#6B7280]">{o.hint}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

function LinkPanel({ group, onChanged }) {
  const link = group.enrolment_link || {};
  const url = enrolmentUrl(link.code);
  const [busy, setBusy] = useState("");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Am copiat linkul.");
    } catch {
      toast.error("Nu am putut copia. Selectează linkul și copiază-l de mână.");
    }
  };
  const act = async (what, call, ok) => {
    setBusy(what);
    try {
      const r = await call();
      onChanged?.(r.data.enrolment_link);
      toast.success(ok);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut schimba linkul."));
    } finally {
      setBusy("");
    }
  };
  const rotate = () => {
    if (!window.confirm("Generezi alt link? Cel vechi nu mai merge: cine îl are va trebui să primească linkul nou.")) return;
    act("rotate", () => api.post(`/admin/groups/${group.id}/link/rotate`), "Linkul nou e gata; cel vechi nu mai merge.");
  };
  const toggle = () => act("toggle", () => api.post(`/admin/groups/${group.id}/link/active`, { active: !link.active }),
    link.active ? "Înscrierile prin link sunt oprite." : "Înscrierile prin link sunt deschise din nou.");

  return (
    <div className="mt-4 rounded-xl bg-[#F5F6F6] p-3" data-testid="group-link-panel">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-[#373A45]">
        <Link2 size={13} /> Linkul de înscriere
        <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
          link.active ? "bg-[#EAF4EE] text-[#2F6B4E]" : "bg-[#FDECEC] text-[#9b1f2c]"}`}>
          {link.active ? "deschis" : "oprit"}
        </span>
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input readOnly value={url} onFocus={(e) => e.target.select()}
          className="min-w-0 flex-1 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 font-mono text-xs"
          data-testid="group-link-url" aria-label="Linkul de înscriere" />
        <button type="button" onClick={copy}
          className="inline-flex items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold hover:border-[#965EA7]">
          <Copy size={12} /> Copiază
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={toggle} disabled={Boolean(busy)}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-[#373A45] hover:text-[#965EA7]"
          data-testid="group-link-toggle">
          {link.active ? <><Pause size={12} /> Oprește înscrierile prin link</> : <><Play size={12} /> Redeschide înscrierile</>}
        </button>
        <button type="button" onClick={rotate} disabled={Boolean(busy)}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-[#373A45] hover:text-[#965EA7]"
          data-testid="group-link-rotate">
          <RefreshCw size={12} /> Generează alt link
        </button>
      </div>
      <p className="mt-2 text-[11px] text-[#6B7280]">
        Trimite linkul doar instituției. Cine îl are se poate înscrie, cât timp grupa are locuri și înscrierile nu s-au închis.
      </p>
    </div>
  );
}
